import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';
import type { IChatMessage } from '@/types/chat';
import { pushAndWait } from '../navigate';
import type { AutopilotFunctionFactory } from '../types';

const RECENT_MESSAGE_LIMIT = 10;
const MESSAGE_PREVIEW_LENGTH = 500;

export const orderSupportWorkflowGuide =
  "The support tab is this order's chat with the campaign management / support team, who read and answer it. " +
  'To contact them: (1) read order_support.recent_messages first — if the same question was already asked, tell the user ' +
  'instead of asking twice; (2) order_support_draft_message puts your text into the message box without sending it; ' +
  '(3) show the draft to the user with ask_user (send / change / cancel); (4) only after an explicit yes, ' +
  'order_support_send_message sends exactly the draft in the message box — including any edits the user typed there. ' +
  'A sent message cannot be taken back.';

/**
 * Snapshot of the order's support chat: the latest messages (oldest first, truncated) and the
 * unsent draft in the message box. Returns `undefined` without an order id so the key is not
 * published empty.
 */
export function toAutopilotOrderSupport(orderId: string | number | undefined, messages: IChatMessage[], draft: string) {
  if (!orderId) return undefined;

  return {
    order_id: Number(orderId) || orderId,
    message_count: messages.length,
    recent_messages: messages.slice(-RECENT_MESSAGE_LIMIT).map((message) => ({
      from: message.actor_string || null,
      is_me: !!message.is_me,
      created_at: message.created_at || null,
      message:
        message.message && message.message.length > MESSAGE_PREVIEW_LENGTH
          ? `${message.message.slice(0, MESSAGE_PREVIEW_LENGTH)}…`
          : message.message || '',
    })),
    draft: draft || null,
  };
}

export interface OrderSupportDeps {
  getDraft: () => string;
  setDraft: (text: string) => void;
  send: () => Promise<IChatMessage | undefined>;
  isSending: () => boolean;
}

/** Draft + send tools for the support tab. Bound to the chat card the tab renders. */
export function createOrderSupportFunctions(deps: OrderSupportDeps): AutopilotFunctionDefinition[] {
  const draftMessage: AutopilotFunctionDefinition = {
    name: 'order_support_draft_message',
    description:
      "Write a message into the support chat's message box WITHOUT sending it, so the user can read it. " +
      'Replaces any previous draft. Afterwards show the draft to the user with ask_user and only send it once they agree.',
    parameters: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description: "The full message text, in the user's language, addressed to campaign management / support.",
        },
      },
      required: ['message'],
    },
    handler: async (rawArgs) => {
      const args = (rawArgs ?? {}) as { message?: string };
      const message = args.message?.trim();
      if (!message) return { success: false, error: 'message is required' };

      deps.setDraft(message);
      return { success: true, draft: message, sent: false };
    },
  };

  const sendMessage: AutopilotFunctionDefinition = {
    name: 'order_support_send_message',
    description:
      'Send the draft that is currently in the support chat message box to campaign management / support. ' +
      'Takes no arguments: it sends exactly what is on screen. Only call it after the user explicitly approved the draft via ask_user. ' +
      'Sends immediately and cannot be undone.',
    parameters: { type: 'object', properties: {} },
    handler: async () => {
      if (deps.isSending()) return { success: false, error: 'A message is already being sent.' };
      if (!deps.getDraft().trim()) {
        return {
          success: false,
          error: 'The message box is empty. Draft a message with order_support_draft_message first.',
        };
      }

      try {
        const sent = await deps.send();
        if (!sent) return { success: false, error: 'Nothing was sent.' };
        return { success: true, message_id: sent.id };
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Failed to send the message' };
      }
    },
  };

  return [draftMessage, sendMessage];
}

/** Opens the support tab of the order on screen; registered by the order details page for all its tabs. */
export function createGotoOrderSupportFunction(orderId: string): AutopilotFunctionFactory {
  return ({ router }) => ({
    name: 'goto_order_support',
    description:
      "Open the support tab of the order on screen (current_order) — the order's chat with campaign management / support. " +
      'This only navigates; it sends nothing. Afterwards read context.order_support and follow order_support_workflow.',
    parameters: { type: 'object', properties: {} },
    handler: async () => {
      await pushAndWait(router, { name: 'order.detail.support', params: { id: orderId } });
      return { success: true, order_id: Number(orderId) || orderId };
    },
  });
}
